import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Actions,
  getClosingBalanceFinancialStats,
  getMonthlyFinancialSummaryStats,
  getRefundStats,
} from "../../../actions/admin.action";
import Alert from "../../../components/alert";
import {
  ClosingBalanceFinancialStats,
  CommercialODFinancialStats,
  MonthlyFinancialSummaryStats,
  MultiMonthFinancialStats,
  OneMonthFinancialStats,
  OverallMonthlyFinancialStatsType,
  RefundStats,
} from "../../../dto";
import { ReactComponent as Loader } from "../../../images/loader.svg";
import { ForbiddenError } from "../../../middleware";
import { ReduxState } from "../../../reducers";
import { isLoadingActive } from "../../../utils";
import InfoCardList from "../infoCardList";
import { ReportSectionAuthStatus } from "../types";
import {
  fetchDataForSelectedMonth,
  getOverallMonthlyFinancialSection,
  getSafeSum,
} from "../utils";

interface OverallMonthlyFinancialStatsProps {
  selectedDate: Date | null;
  onAuthorizationChange: ReportSectionAuthStatus;
}

export default function OverallMonthlyFinancialStats({
  selectedDate,
  onAuthorizationChange,
}: OverallMonthlyFinancialStatsProps) {
  const storeDispatch = useDispatch();
  const loadingQueue = useSelector(
    (state: ReduxState) => state.admin.loadingQueue,
  );
  const oneMonthFinancialStats: OneMonthFinancialStats | null = useSelector(
    (state: ReduxState) => state.admin.oneMonthFinancialStats,
  );
  const multiMonthFinancialStats: MultiMonthFinancialStats | null = useSelector(
    (state: ReduxState) => state.admin.multiMonthFinancialStats,
  );
  const commercialOdFinancialStats: CommercialODFinancialStats | null =
    useSelector((state: ReduxState) => state.admin.commercialOdFinancialStats);
  const closingBalancesFinancialStats: ClosingBalanceFinancialStats | null =
    useSelector(
      (state: ReduxState) => state.admin.closingBalanceFinancialStats,
    );
  const monthlyFinancialSummaryStats: MonthlyFinancialSummaryStats | null =
    useSelector(
      (state: ReduxState) => state.admin.monthlyFinancialSummaryStats,
    );
  const refundStats: RefundStats | null = useSelector(
    (state: ReduxState) => state.admin.refundStats,
  );

  const [errorMessage, setErrorMessage] = useState("");
  const [notAuthorized, setNotAuthorized] = useState(false);

  useEffect(() => {
    if (!selectedDate) return;
    let forbidden = false;
    let failed = false;
    const fetchers = [
      fetchDataForSelectedMonth(
        getClosingBalanceFinancialStats,
        storeDispatch,
        selectedDate,
      ),
      fetchDataForSelectedMonth(
        getMonthlyFinancialSummaryStats,
        storeDispatch,
        selectedDate,
      ),
      fetchDataForSelectedMonth(getRefundStats, storeDispatch, selectedDate),
    ];
    Promise.all(
      fetchers.map((fetchPromise) =>
        fetchPromise.catch((error) => {
          failed = true;
          if (error instanceof ForbiddenError) forbidden = true;
        }),
      ),
    ).then(() => {
      if (forbidden) {
        setNotAuthorized(true);
        onAuthorizationChange("overall", "forbidden");
      } else if (failed) {
        onAuthorizationChange("overall", "error");
      } else {
        onAuthorizationChange("overall", "authorized");
      }
    });
  }, [selectedDate, storeDispatch, onAuthorizationChange]);

  const isLoading = isLoadingActive(loadingQueue, [
    Actions.REQUEST_GET_CLOSING_BALANCE_FINANCIAL_STATS,
    Actions.REQUEST_GET_MONTHLY_FINANCIAL_SUMMARY_STATS,
    Actions.REQUEST_GET_REFUND_STATS,
    Actions.REQUEST_GET_ONE_MONTH_FINANCIAL_STATS,
    Actions.REQUEST_GET_MULTI_MONTH_FINANCIAL_STATS,
    Actions.REQUEST_GET_COMMERCIAL_OD_FINANCIAL_STATS,
  ]);

  const overallTotalDisbursedAmount = useMemo(() => {
    return getSafeSum(
      oneMonthFinancialStats?.data?.totalDisbursedAmount,
      multiMonthFinancialStats?.data?.totalDisbursedAmount,
      commercialOdFinancialStats?.data?.totalDisbursedAmount,
    );
  }, [
    oneMonthFinancialStats?.data?.totalDisbursedAmount,
    multiMonthFinancialStats?.data?.totalDisbursedAmount,
    commercialOdFinancialStats?.data?.totalDisbursedAmount,
  ]);

  const overallTotalCollections = useMemo(() => {
    return getSafeSum(
      oneMonthFinancialStats?.data?.totalRepaidAmount,
      multiMonthFinancialStats?.data?.totalRepaidAmount,
      commercialOdFinancialStats?.data?.totalPrincipalRepaid,
      commercialOdFinancialStats?.data?.totalInterestRepaid,
    );
  }, [
    oneMonthFinancialStats?.data?.totalRepaidAmount,
    multiMonthFinancialStats?.data?.totalRepaidAmount,
    commercialOdFinancialStats?.data?.totalPrincipalRepaid,
    commercialOdFinancialStats?.data?.totalInterestRepaid,
  ]);

  const overallTotalProcessingFees = useMemo(() => {
    return getSafeSum(
      oneMonthFinancialStats?.data?.totalProcessingFee,
      multiMonthFinancialStats?.data?.totalProcessingFee,
      commercialOdFinancialStats?.data?.totalProcessingFeeCharged,
    );
  }, [
    oneMonthFinancialStats?.data?.totalProcessingFee,
    multiMonthFinancialStats?.data?.totalProcessingFee,
    commercialOdFinancialStats?.data?.totalProcessingFeeCharged,
  ]);

  const overallTotalInterestEarned = useMemo(() => {
    return getSafeSum(
      oneMonthFinancialStats?.data?.totalRepaidInterest,
      multiMonthFinancialStats?.data?.totalRepaidInterest,
      commercialOdFinancialStats?.data?.totalInterestRepaid,
    );
  }, [
    oneMonthFinancialStats?.data?.totalRepaidInterest,
    multiMonthFinancialStats?.data?.totalRepaidInterest,
    commercialOdFinancialStats?.data?.totalInterestRepaid,
  ]);

  const overallTotalPrincipalCollected = useMemo(() => {
    return getSafeSum(
      oneMonthFinancialStats?.data?.totalRepaidPrincipal,
      multiMonthFinancialStats?.data?.totalRepaidPrincipal,
      commercialOdFinancialStats?.data?.totalPrincipalRepaid,
    );
  }, [
    oneMonthFinancialStats?.data?.totalRepaidPrincipal,
    multiMonthFinancialStats?.data?.totalRepaidPrincipal,
    commercialOdFinancialStats?.data?.totalPrincipalRepaid,
  ]);

  const overallTotalTdsDeducted = getSafeSum(
    commercialOdFinancialStats?.data?.totalTdsChargedOnInterest,
    commercialOdFinancialStats?.data?.totalTdsChargedOnProcessingFee,
  );

  const overallWeightedAverageApr = useMemo(() => {
    // APR is a ratio, not a sum: partial sums would mix different slice
    // coverage into the numerator and denominator, so require both factor
    // slices to be present before computing it.
    const oneMonthFactor = oneMonthFinancialStats?.data?.weightedAverageAprFactor;
    const multiMonthFactor =
      multiMonthFinancialStats?.data?.weightedAverageAprFactor;
    if (
      typeof oneMonthFactor !== "number" ||
      !Number.isFinite(oneMonthFactor) ||
      typeof multiMonthFactor !== "number" ||
      !Number.isFinite(multiMonthFactor) ||
      overallTotalDisbursedAmount === undefined ||
      overallTotalDisbursedAmount === 0
    ) {
      return undefined;
    }
    return (oneMonthFactor + multiMonthFactor) / overallTotalDisbursedAmount;
  }, [
    oneMonthFinancialStats?.data?.weightedAverageAprFactor,
    multiMonthFinancialStats?.data?.weightedAverageAprFactor,
    overallTotalDisbursedAmount,
  ]);

  if (isLoading) {
    return (
      <div className="d-flex align-items-center justify-content-center dashboard-container p-3">
        <Loader />
      </div>
    );
  }

  if (notAuthorized) {
    return (
      <div className="dashboard-container">
        <Alert
          type="danger"
          message="Not authorized to view overall monthly financials."
        />
      </div>
    );
  }

  if (
    !oneMonthFinancialStats ||
    !multiMonthFinancialStats ||
    !closingBalancesFinancialStats ||
    !monthlyFinancialSummaryStats ||
    !refundStats
  ) {
    // TODO: This component relies on another component's dispatch and we have to address this in the future
    return (
      <Alert
        message="Failed to load financial stats. Please try again later."
        type="info"
      />
    );
  }

  const data: OverallMonthlyFinancialStatsType = {
    overallTotalDisbursedAmount: overallTotalDisbursedAmount,
    overallTotalProcessingFees: overallTotalProcessingFees,
    closingBalance: closingBalancesFinancialStats.closingBalance,
    overallTotalTdsDeducted: overallTotalTdsDeducted,
    overallTotalCollections: overallTotalCollections,
    overallTotalPrincipalCollected: overallTotalPrincipalCollected,
    overallTotalInterestEarned: overallTotalInterestEarned,
    totalCollectionWriteOffAmountForMonth:
      monthlyFinancialSummaryStats.data.totalCollectionWriteOffAmountForMonth,
    unpaidCommitment: monthlyFinancialSummaryStats.data.unpaidCommitment,
    totalInsurancePremiumWithGst:
      monthlyFinancialSummaryStats.data.totalInsurancePremiumWithGst,
    closingBalanceWrittenOffLoans:
      monthlyFinancialSummaryStats.data.closingBalanceWrittenOffLoans,
    totalRefunds: refundStats.data.totalRefunds,
    overallWeightedAverageApr,
  };

  const { infoCards, sectionName } = getOverallMonthlyFinancialSection(data);

  return (
    <div className="dashboard-container">
      <Alert
        message={errorMessage}
        type="info"
        canDismiss
        onDismissClick={() => setErrorMessage("")}
      />

      <section>
        <h3 className="user-page-title">{sectionName}</h3>
        <InfoCardList infoCards={infoCards} />
      </section>
    </div>
  );
}
